import { Amount, asAmount } from "@goodfill/domain-properties";
import { Field, InputNumber, Span, Spinner } from "../../../ui-components";
import { OrderFormState } from "../../order/orderForm/state/OrderFormState";
import {
  AccruedInterestAmountComponent,
  FaceAmountComponent,
  formatMinimumInvestment,
  SettlementAmountComponent,
  SettlementDateComponent,
  TradeDateComponent,
} from "@goodfill/domain-properties-components";
import { InfoTooltip } from "../../../ui-components/tooltip/InfoTooltip";
import { GrayBox } from "../../../ui-components/GrayBox";
import "./BondDetailSimulator.scss";
import Decimal from "decimal.js";
import { FieldGroup } from "../../../form/FieldGroup";
import { numberFormat } from "../../../number/numberFormat";
import { createFiniteDecimalFromAnyStringNonStrict } from "@goodfill/decimal";
import { input } from "aws-amplify";
import { TIMEZONE_NEY_YORK } from "../../../locale/timezone";
import { useState, useEffect } from "react";

interface Props {
  state: OrderFormState | undefined;
  onFaceAmountChange: (value: Amount) => void;
}

export const BondDetailSimulator: React.FC<Props> = ({ state, onFaceAmountChange }) => {
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (state !== undefined) {
      setInputValue(state.s.faceAmount.amount.toFixed(0));
    }
  }, [state]);

  if (state === undefined) {
    return (
      <div className="flex flex-col gap-4 md:gap-8 w-1/2">
        <Spinner />
      </div>
    );
  }

  const isValid = (value: string) =>
    createFiniteDecimalFromAnyStringNonStrict(`${value}`)?.greaterThanOrEqualTo(
      state.i.pricedBond.minimumPiece.amount,
    ) ?? false;

  const validation = !isValid(inputValue)
    ? [`Minimum Face Amount: ${formatMinimumInvestment({ value: state.i.pricedBond.minimumPiece.amount })}`]
    : [];

  return (
    <div className="flex flex-col gap-4 md:gap-8">
      <FieldGroup validation={validation} className="mt-0-important mb-0-important gap-0" isCol={false} fill={true}>
        <InputNumber
          leftIcon="$"
          id="faceAmount"
          value={parseInt(inputValue) / 1000}
          setValue={(value) => {
            if (value === null) return;
            value = Number(value) * 1000;
            setInputValue(`${value}`);
            if (isValid(`${value}`)) {
              onFaceAmountChange(asAmount(new Decimal(value)));
            }
          }}
          textAlign="left"
          border={"neutral"}
          formatValue={numberFormat}
          placeholder="Enter Face Amount"
        />
        <Span style={{ color: "rgba(51, 63, 81, 0.6)" }} className="px-0">
          ,000
        </Span>
        <div className="face-amount-tooltip">
          <InfoTooltip text={"Minimum value is [Minimum Face Amount / $1000, ie. “1” or “2”]"} />
        </div>
      </FieldGroup>
      <GrayBox className="w-ful flex flex-col gap-4 md:p-4 p-2">
        <div className="grid lg:grid-cols-3 grid-cols-2 md:gap-4 gap-2">
          <Field
            label={"Trade Date"}
            textAlign="left"
            labelClassName="whitespace-unset-important"
            valueClassName="whitespace-unset-important"
          >
            <TradeDateComponent value={state.i.tradeDate} timezone={TIMEZONE_NEY_YORK} />
          </Field>
          <Field label={"Settlement Date"} textAlign="left" labelClassName="whitespace-unset-important">
            <SettlementDateComponent value={state.i.settlementDate} timezone={TIMEZONE_NEY_YORK} />
          </Field>
        </div>

        <div className="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 md:gap-4 gap-2">
          <Field
            label={
              <>
                Actual Investment <InfoTooltip text="Actual Investment = Offer Price% × Face Value. " />
              </>
            }
            textAlign="left"
            labelClassName="whitespace-unset-important"
          >
            <div className="flex items-stretch">
              <div>
                <FaceAmountComponent value={asAmount(state.s.faceAmount.amount.mul(state.s.customLoadedPrice))} />
              </div>
              <div className="text-right w-full md:block hidden">+</div>
            </div>
          </Field>

          <Field label={"Accrued Interest"} textAlign="left" labelClassName="whitespace-unset-important">
            <div className="flex items-stretch mt-[4px] BondDetailSimulator_accruedInterestField">
              <div>
                <AccruedInterestAmountComponent value={state.c.accruedInterest} />
              </div>
              <div className="text-right w-full lg:block hidden">=</div>
            </div>
          </Field>

          <Field
            label={"Settlement Proceeds"}
            textAlign="left"
            labelClassName="whitespace-unset-important"
            valueClassName="mt-[4px] BondDetailSimulator_settlementAmountField"
          >
            <SettlementAmountComponent value={state.settlementAmount} />
          </Field>
        </div>
      </GrayBox>
    </div>
  );
};
